import Settings from '~/settings.js';
import Parse from 'parse';
import Backbone from 'backbone';
import ProgressService from "~/services/progressService";
import HashService from '~/services/hashService';

/**
 * @constructor
 */
function ParseService() {
  this.initialize();
}
/**
 * extend helper from Backbone
 */
ParseService.extend = Backbone.Model.extend;
/**
 * shared between all Parse services (used to initialize Parse settings one time only)
 * @type {boolean}
 */
var isInitialized = false;
/**
 * @function ParseService.prototype
 * @type {{cache: {}, itemsOnPage: number, cacheMinutes: number, initialize: ParseService.initialize, _getQuery: ParseService._getQuery, _getFullQuery: ParseService._getFullQuery, _processRules: ParseService._processRules, _fetchPage: ParseService._fetchPage, _fetchRecursive: ParseService._fetchRecursive, generateCacheKey: ParseService.generateCacheKey, getCachedValue: ParseService.getCachedValue, addCachedValue: ParseService.addCachedValue, fetch: ParseService.fetch, fetchById: ParseService.fetchById, save: ParseService.save}}
 */
ParseService.prototype = {
    /**
     * used for caching collection
     * @member [class] cache
     */
    cache: {}, // used for caching collection
    /**
     * max number of items in response
     * @member [Number] itemsOnPage
     */
    itemsOnPage: 100000, // max number of items in response
    /**
     * default cache for collections, can be overriden in service class
     * @member [Number] cacheMinutes
     */
    cacheMinutes: 120, // default cache for collections, can be overriden in service class

    /**
     * initialize function starts if this object not has initialized, set it true
     * and set Parse properties
     * @function initialize
     * @memberOf ParseService.prototype
     */
    initialize: function() {
      if (!isInitialized) {
        isInitialized = true;
        Parse.serverURL = Settings.parseUrl;
        Parse.initialize(Settings.parseAppId, Settings.parseKey);
      }
    },

    /**
     * Set properties on Parse queries with pagination
     * @function _getQuery
     * @param pageNumber
     * @param settings
     * @returns [Object Model]
     * @private
     * @memberOf ParseService.prototype
     */

    _getQuery: function(pageNumber = 0, settings) {
      var query;
      if (settings.rules && settings.rules.or) {
        query = settings.rules.toParseQuery(this.model);
      } else {
        query = new Parse.Query(this.model);
      }
      query.limit(this.itemsOnPage);
      if(settings.rules && settings.rules.sort)
      {
        if(settings.rules.sort.val == 'dsc'){
          query.descending(settings.rules.sort.key);
        }
        else {
          query.ascending(settings.rules.sort.key);
        }
      }else{
        if(this.order && this.order == 'dsc'){
          query.descending(this.sortBy);
        }
        else {
          query.ascending(this.sortBy);
        }
      }
      query.skip(this.itemsOnPage * pageNumber); //default 0
      query = this._processRules(query, settings.rules);
      return query;
    },

    /**
     * Set properties on Parse queries without pagination
     * @function _getFullQuery
     * @param settings
     * @returns [Object Model]]
     * @private
     * @memberOf ParseService.prototype
     */
    _getFullQuery: function(settings) {
      var query;
      if (settings.rules && settings.rules.or) {
        query = settings.rules.toParseQuery(this.model);
      } else {
        query = new Parse.Query(this.model);
      }
      query.limit(this.itemsOnPage);
      query.ascending(this.sortBy);
      query = this._processRules(query, settings.rules);
      return query;

    },

    /**
     * Set queries a dictionary of rules by  processes
     * means that it sets properties as need it on queries
     * @function _processRules
     * @param query
     * @param rules
     * @returns [Object Model]
     * @private
     * @memberOf ParseService.prototype
     */
    _processRules: function(query, rules) {
      if (rules) {
        for (var key in rules.includes) {
          if (rules.includes.hasOwnProperty(key)) {
            query.include(rules.includes);
          }
        }
        for (var i = 0; rules.equals && i < rules.equals.length; ++i) {
          query.equalTo(rules.equals[i].key, rules.equals[i].val);
        }
        for (var i = 0; rules.notEquals && i < rules.notEquals.length; ++i) {
          query.notEqualTo(rules.notEquals[i].key, rules.notEquals[i].val);
        }
        for (var key in rules.greaterThanOrEqualTo) {
          if (rules.greaterThanOrEqualTo.hasOwnProperty(key)) {
            query.greaterThanOrEqualTo(key, rules.greaterThanOrEqualTo[key]);
          }
        }
        for (var key in rules.lessThanOrEqualTo) {
          if (rules.lessThanOrEqualTo.hasOwnProperty(key)) {
            query.lessThanOrEqualTo(key, rules.lessThanOrEqualTo[key]);
          }
        }
        for (var key in rules.contains) {
          if (rules.contains.hasOwnProperty(key)) {
            query.contains(key, rules.contains[key]);
          }
        }
        for (var key in rules.matchesQuery) {
          if (rules.matchesQuery.hasOwnProperty(key)) {
            query.matchesQuery(key, rules.matchesQuery[key].toParseQuery());
          }
        }
      }
      return query;
    },

    _processIncludes: function(query, includes) {
      if (includes) {
        for (var i = 0; i < includes.length; ++i) {
          query.include(includes[i]);
        }
      }
      return query;
    },
    // fetch Parse data for one page only

    /**
     * fetch queries data by pages only
     * @function _fetchPage
     * @param collection
     * @param pageNumber
     * @param settings
     * @returns [collection, models]
     * @private
     * @memberOf ParseService.prototype
     */
    _fetchPage: function(collection, pageNumber = 0, settings) {
      var query = this._getQuery(pageNumber, settings);
      if (query) {
        ProgressService.increment();
        return query.find().then(models => {
          ProgressService.decrement();
          try {
            collection.add(models);
          } catch (e) {
            console.error(e);
          }
          return [collection, models];
        });
      } else {
        return Parse.Promise.error("No query");
      }

    },

    /**
     * recursive fetch to load all data on queries at once
     * else throws error
     * @function _fetchRecursive
     * @param collection
     * @param page
     * @param settings
     * @returns [collection, models]
     * @private
     * @memberOf ParseService.prototype
     */
    _fetchRecursive: function(collection, page, settings) {
      return new Parse.Promise((resolve, reject) => {
        this._fetchPage(collection, page, settings).then(([collection,
          models
        ]) => {
          if (settings && settings.all && models.length && models.length ==
            this.itemsOnPage) {
            // load next page of data
            this._fetchRecursive(collection, ++page, settings).then(
              args => {
                // resolve promise if 'waitAll' is set to 'true' (promise will be resolved when all data will be loaded)
                if (settings && settings.all && settings.waitAll) {
                  resolve(args);
                }
              }, err => {
                reject(err);
              });
          } else {
            // resolve if this is last page of data (e.g. number of models is less than 'itemsOnPage')
            collection.trigger('isLoading', false); // is loading data
            resolve([collection, models]);
            return;
          }

          // resolve promise if 'waitAll' is not set to 'true' (promise will be resolved when first page will be loaded)
          if (!settings || !settings.all || !settings.waitAll) {
            resolve([collection, models]);
          }
        });
      }, err => {
        reject(err);
      });
    },

    /**
     * generate cache key, an encrypted data
     * @todo improve key generation so it's generated from the query
     * @function generateCacheKey
     * @param settings
     * @returns {string}
     * @memberOf ParseService.prototype
     */
    generateCacheKey: function(settings) {
      // here is the most simple cache were we always returns the same collection (sure for specific service only)
      // for some services we can implement complex logic (for example with some merge logic or by userid)
      return HashService.value(this._getFullQuery(settings));
    },

    /**
     * search result in cache
     * @function getCachedValue
     * @param settings
     * @memberOf ParseService.prototype
     */
    getCachedValue: function(settings) {
      var key = this.generateCacheKey(settings);
      var cachedRecord = this.cache[key];
      if (cachedRecord) {
        if (cachedRecord.expData >= new Date()) {
          return cachedRecord.collection; // item is not expired, so we return it
        } else {
          delete this.cache[key]; // item is expired, so we remove it from cache
        }
      }
    },

    /**
     * save result to cache
     * @function addCachedValue
     * @param collection
     * @param settings
     * @memberOf ParseService.prototype
     */
    addCachedValue: function(collection, settings) {
      var expDate = new Date();
      expDate.setMinutes(expDate.getMinutes() + this.cacheMinutes); // cache data for 10 minutes (can be overriden globally in this file or specifically for some service)

      this.cache[this.generateCacheKey(settings)] = {
        collection: collection,
        expData: expDate
      }
    },

    // fetch items from Parse
    // settings:
    // {
    //   page: numeric (default is 0)
    //   all: boolean (default is false) - load all items. promise will be resolved right after returning items for the first page
    //   waitAll: boolean(default is false) - resolves promise only when all pages will be received
    //   cache: boolean(default is false) - caches collection
    // }
    /**
     * @function fetch
     * @param settings
     * @returns [Parse.Promise]
     * @memberOf ParseService.prototype
     */
    fetch: function(settings) {

      /**
       * check cache for cached value
       */
      if (settings && settings.cache) {
        var cachedValue = this.getCachedValue(settings);
        if (cachedValue) {
          return new Parse.Promise((resolve, reject) => {
            resolve(cachedValue);
          }, (err) => reject(err));
        }
      }

      return new Parse.Promise((resolve, reject) => {
        /**
         * create collection
         */
        var collection = new this.collection();
        /**
         * is loading data
         */
        collection.trigger('isLoading', true);
        /**
         * set page
         * @type {*}
         */
        var page = (settings && settings.page) || 0;
        /**
         *
         */
        this._fetchRecursive(collection, page, settings).then(([
          collection, models
        ]) => {
          if (settings && settings.cache) {
            this.addCachedValue(collection, settings); // cache value
          }
          resolve(collection); // resolve promise
        }, (err) => reject(err));
      });
    },
    /**
     * @function fetchById
     * @param id
     * @param settings
     * @memberOf ParseService.prototype
     */
    fetchById: function(id, settings) {
      var query = new Parse.Query(this.model);
      return query.get(id);
    },
    /**
     * @function save
     * @param model
     * @memberOf ParseService.prototype
     */
    save: function(model) {
      model.save(null, {
        /**
         * @function success
         */
        success: function() {},
        /**
         * @function error
         * @param e
         */
        error: function(e) {
          //  console.log('error: ' + e);
        }
      });
    }
  }
  /**
   * this is base service for all Parse operations
   * Module parseService
   * @module services/parseService
   * @returns ParseService
   */
export default ParseService;
